import { supabase } from '../config/supabase';

/**
 * Save a service log entry to Supabase service_logs table.
 * THROWS on failure so callers can queue retries via writeQueue.
 *
 * @param {string} userId - Supabase auth user ID
 * @param {string} vehicleId - localStorage vehicle ID (stored as reference)
 * @param {object} entry - Service entry data
 */
export async function saveServiceLog(userId, vehicleId, entry) {
  if (!userId) {
    console.warn('[PM-Write] No userId, skipping service log save');
    return null;
  }

  const row = {
    user_id: userId,
    vehicle_id: vehicleId,
    service_type: entry.service || 'Unknown service',
    service_date: entry.date || new Date().toISOString().split('T')[0],
    mileage: entry.mileage ? Number(entry.mileage) : null,
    cost: entry.cost != null ? Number(entry.cost) : 0,
    shop_type: mapShopType(entry.type),
    shop_name: entry.shopName || null,
    notes: entry.notes || null,
  };

  const { data, error } = await supabase
    .from('service_logs')
    .insert(row)
    .select()
    .single();

  if (error) throw error;
  console.log('[PM-Write] Service log saved:', data?.id);
  return data;
}

/**
 * Save a mileage update to Supabase service_logs table.
 * THROWS on failure so callers can queue retries.
 */
export async function saveMileageLog(userId, vehicleId, entry) {
  if (!userId) {
    console.warn('[PM-Write] No userId, skipping mileage log save');
    return null;
  }

  const row = {
    user_id: userId,
    vehicle_id: vehicleId,
    service_type: 'mileage_update',
    service_date: entry.date || new Date().toISOString().split('T')[0],
    mileage: entry.mileage ? Number(entry.mileage) : null,
    cost: 0,
    shop_type: null,
    shop_name: null,
    notes: entry.notes || null,
  };

  const { data, error } = await supabase
    .from('service_logs')
    .insert(row)
    .select()
    .single();

  if (error) throw error;
  console.log('[PM-Write] Mileage log saved:', data?.id);
  return data;
}

/**
 * Update a vehicle's current_mileage and last_mileage_update_at in Supabase.
 * THROWS on failure so callers can queue retries.
 */
export async function updateVehicleMileage(userId, vehicleId, mileage) {
  if (!userId || !vehicleId) return;

  const { error } = await supabase
    .from('vehicles')
    .update({
      current_mileage: Number(mileage),
      last_mileage_update_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .eq('id', vehicleId);

  if (error) throw error;
  console.log('[PM-Write] Vehicle mileage updated in Supabase:', vehicleId);
}

/**
 * Load service logs from Supabase for a specific user.
 * Returns entries sorted by service_date descending.
 *
 * @param {string} userId - Supabase auth user ID
 * @param {string} [vehicleId] - Optional vehicle filter
 * @param {number} [limit=50] - Max entries to return
 */
export async function loadServiceLogs(userId, vehicleId, limit = 50) {
  if (!userId) return [];

  try {
    let query = supabase
      .from('service_logs')
      .select('*')
      .eq('user_id', userId)
      .order('service_date', { ascending: false })
      .limit(limit);

    if (vehicleId) {
      query = query.eq('vehicle_id', vehicleId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[ServiceLog] Load failed:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('[ServiceLog] Load error:', err.message);
    return [];
  }
}

/**
 * Load all service logs for a specific vehicle via RPC (bypasses RLS).
 * Used for shared garage vehicles where the logged-in user may not be the owner.
 * Returns raw Supabase rows — use mapServiceLogToLocal() to convert to localStorage format.
 *
 * @param {string} vehicleId - Vehicle UUID
 * @returns {Promise<Array>}
 */
export async function loadVehicleServiceLogs(vehicleId) {
  if (!vehicleId) return [];
  try {
    const { data, error } = await supabase.rpc('get_vehicle_service_logs', {
      vehicle_uuid: vehicleId,
    });
    if (error) throw error;
    console.log('[PM-Read] Loaded', data?.length || 0, 'service logs for vehicle:', vehicleId);
    return data || [];
  } catch (err) {
    console.error('[PM-Read] loadVehicleServiceLogs failed:', err.message);
    return [];
  }
}

/**
 * Map a Supabase service_logs row to the localStorage service entry format.
 * Used in hydration, invite acceptance, and on-demand loading for shared vehicles.
 *
 * @param {object} log - Supabase service_logs row
 * @returns {object} localStorage-format service entry
 */
export function mapServiceLogToLocal(log) {
  return {
    id: log.id,
    vehicleId: log.vehicle_id,
    service: log.service_type,
    date: log.service_date,
    mileage: log.mileage,
    cost: log.cost,
    type: log.shop_type === 'diy' ? 'DIY'
      : log.shop_type === 'independent' ? 'Independent'
      : log.shop_type === 'dealer' ? 'Dealer'
      : null,
    shopName: log.shop_name,
    notes: log.notes,
  };
}

/**
 * Map UI shop type values to database shop_type values.
 */
function mapShopType(uiType) {
  if (!uiType) return null;
  const map = {
    'DIY': 'diy',
    'Independent': 'independent',
    'Dealer': 'dealer',
  };
  return map[uiType] || uiType.toLowerCase();
}
